import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, type FC } from 'react';
import { useDispatch } from 'react-redux';

import { useDynamicReducer } from '@/core/applicationState/hooks/useDynamicReducer';
import { useDynamicSaga } from '@/core/applicationState/hooks/useDynamicSaga';
import { useDetail } from '@/productDiscovery/ProductPage/hooks/useDetail';
import RecosContainerA2C from '@/productDiscovery/Recommendation/components/RecosContainerA2C/RecosContainerA2C';
import { hideAddToCartModal } from '@/shoppingCart/AddToCart/AddToCart.actions';
import { addToCartReducer } from '@/shoppingCart/AddToCart/AddToCart.reducer';
import { rootAddToCartSaga } from '@/shoppingCart/AddToCart/AddToCart.root.saga';
import { AddToCartModalScaffold } from '@/shoppingCart/AddToCart/components/BigAddToCartModal/AddToCartModalScaffold';
import { useAddedToCartModal } from '@/shoppingCart/hooks/useAddedToCartModal';

const CrossPlatformPurchaseBlockedModal = dynamic(
  import(
    '@/domains/shoppingCart/AddToCart/components/CrossPlatformPurchaseBlockedModal/CrossPlatformPurchaseBlockedModal'
  ).then((mod) => mod.CrossPlatformPurchaseBlockedModal),
  { ssr: false },
);

const Product = dynamic(
  import(
    '@/shoppingCart/AddToCart/components/BigAddToCartModal/AddToCartModalProduct'
  ).then((mod) => mod.AddToCartModalProduct),
  { ssr: false },
);
const MainProduct = dynamic(
  import(
    '@/productDiscovery/ProductPage/common/components/AddToCart/MainProduct'
  ).then((mod) => mod.MainProduct),
  { ssr: false },
);

export const AddToCartModal: FC = () => {
  // TODO: move this to ProductContainer Level
  useDynamicReducer('shoppingCart.addToCartModal', addToCartReducer);
  useDynamicSaga('addToCart', rootAddToCartSaga);
  const { categoryId } = useDetail();

  const dispatch = useDispatch();

  const modalState = useAddedToCartModal();

  const closeModal = () => {
    dispatch(hideAddToCartModal());
  };

  const { asPath } = useRouter();
  useEffect(() => {
    dispatch(hideAddToCartModal());
  }, [asPath, dispatch]);

  if (modalState === undefined) return null;

  const { products, errors, isVisible, showCrossBorderPurchaseBlockedModal } =
    modalState;

  const productError = errors.find((error) => error.id === products.at(0)?.id);

  if (showCrossBorderPurchaseBlockedModal) {
    return <CrossPlatformPurchaseBlockedModal />;
  }

  if (!isVisible) return null;
  const [product] = modalState.products;
  if (modalState.displayAddToCartCustomSummary) {
    return (
      <AddToCartModalScaffold {...modalState} onClose={closeModal}>
        <Product product={product} error={productError} />
        <RecosContainerA2C categoryId={categoryId} />
      </AddToCartModalScaffold>
    );
  }

  return (
    <AddToCartModalScaffold {...modalState} onClose={closeModal}>
      <MainProduct error={productError} />
      <RecosContainerA2C categoryId={categoryId} />
    </AddToCartModalScaffold>
  );
};
